import { useRef, useState } from 'react';
import {
  Check,
  Copy,
  Mail,
  MessageCircle,
  MessageSquare,
  Send,
  Share2,
} from 'lucide-react';
import { useClickOutside } from '../hooks';
import {
  canShareNatively,
  share,
  type ShareChannel,
  type ShareContent,
} from './shareTargets';

/**
 * Share button with a channel menu — WhatsApp, Telegram, email, SMS, copy link,
 * and the OS share sheet where the browser supports it.
 *
 * Presentational and self-contained: it takes the finished content and reports
 * outcomes through `onResult`, so the caller owns toasts and the URL it wants
 * shared. That keeps one component usable from products, orders and invoices
 * without knowing anything about them.
 */

export interface ShareMenuProps {
  content: ShareContent;
  /** Button label. Hidden on narrow screens when `compact`. */
  label?: string;
  compact?: boolean;
  disabled?: boolean;
  /**
   * Shown above the channel list — used to warn that a link points somewhere
   * the recipient cannot reach (a localhost URL, typically).
   */
  warning?: string | null;
  onResult?: (
    channel: ShareChannel,
    result: 'opened' | 'copied' | 'dismissed' | 'unsupported',
  ) => void;
}

const CHANNELS: Array<{ id: ShareChannel; label: string; Icon: typeof Share2 }> = [
  { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
  { id: 'telegram', label: 'Telegram', Icon: Send },
  { id: 'email', label: 'Email', Icon: Mail },
  { id: 'sms', label: 'SMS', Icon: MessageSquare },
  { id: 'copy', label: 'Copy link', Icon: Copy },
];

export function ShareMenu({
  content,
  label = 'Share',
  compact = false,
  disabled = false,
  warning = null,
  onResult,
}: ShareMenuProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const go = async (channel: ShareChannel) => {
    try {
      const result = await share(channel, content);
      if (result === 'copied') {
        // Brief inline confirmation — a clipboard write is otherwise invisible.
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      }
      onResult?.(channel, result);
    } catch {
      onResult?.(channel, 'unsupported');
    } finally {
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-surface-card px-3 text-sm font-medium text-content-primary hover:bg-surface-card-hover disabled:opacity-40"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {copied ? <Check className="h-4 w-4 text-success" /> : <Share2 className="h-4 w-4" />}
        <span className={compact ? 'hidden sm:inline' : ''}>{copied ? 'Copied' : label}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 min-w-[220px] rounded-md border border-border bg-surface-card py-1 shadow-xl"
        >
          {warning && (
            <p className="mx-2 mb-1 rounded bg-warning/10 px-2 py-1 text-[11px] text-warning">
              {warning}
            </p>
          )}

          {canShareNatively() && (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => go('native')}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-card-hover"
              >
                <Share2 className="h-4 w-4 text-content-muted" />
                More apps…
              </button>
              <div className="my-1 border-t border-border" />
            </>
          )}

          {CHANNELS.map(({ id, label: l, Icon }) => (
            <button
              key={id}
              type="button"
              role="menuitem"
              onClick={() => go(id)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-card-hover"
            >
              <Icon className="h-4 w-4 text-content-muted" />
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
